import {SYNC_SERVER} from 'react-native-dotenv';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {Session} from 'src/models';
import {
  isDateRangeSynced,
  addSyncedDateRange,
  getMissingDateRanges,
} from './session-date-tracker';

interface FetchSessionsByDateParams {
  database: any;
  startDate: Date;
  endDate: Date;
}

/**
 * Fetch and hydrate sessions for a specific date range
 * This bypasses WatermelonDB sync to avoid conflicts with interval syncs
 * Uses manual hydration pattern like fetch-session endpoint
 *
 * Caches synced date ranges to avoid unnecessary network requests
 */
export const fetchSessionsByDate = async ({
  database,
  startDate,
  endDate,
}: FetchSessionsByDateParams): Promise<void> => {
  const startTimestamp = startDate.getTime();
  const endTimestamp = endDate.getTime();

  // Check if this date range has already been synced
  const alreadySynced = await isDateRangeSynced(startTimestamp, endTimestamp);
  if (alreadySynced) {
    if (__DEV__) {
      console.log(
        `[fetchSessionsByDate] Date range already synced: ${new Date(
          startTimestamp,
        ).toISOString()} to ${new Date(endTimestamp).toISOString()}`,
      );
    }
    return;
  }

  // Check for missing sub-ranges (partial overlap with synced ranges)
  const missingRanges = await getMissingDateRanges(
    startTimestamp,
    endTimestamp,
  );

  if (missingRanges.length === 0) {
    if (__DEV__) {
      console.log(
        '[fetchSessionsByDate] All sub-ranges already synced, nothing to fetch',
      );
    }
    return;
  }

  try {
    // Fetch each missing range
    for (const {startDate: start, endDate: end} of missingRanges) {
      await fetchAndHydrateSessions(database, start, end);
      // Mark this sub-range as synced
      await addSyncedDateRange(start, end);
    }
  } catch (error) {
    console.error(
      `Failed to fetch sessions for range ${new Date(
        startTimestamp,
      ).toISOString()} to ${new Date(endTimestamp).toISOString()}:`,
      error,
    );
    throw error;
  }
};

/**
 * Fetch sessions from backend and manually hydrate into WatermelonDB
 * This does NOT use the sync endpoint to avoid mutex conflicts
 */
async function fetchAndHydrateSessions(
  database: any,
  startTimestamp: number,
  endTimestamp: number,
): Promise<void> {
  const accessToken = await AsyncStorage.getItem('accessToken');

  if (!accessToken) {
    throw new Error('No access token found');
  }

  const url = `${SYNC_SERVER}/rehydrate-sessions?startTimestamp=${startTimestamp}&endTimestamp=${endTimestamp}`;

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Fetch failed with status ${response.status}`);
    }

    const data = await response.json();
    const fetchedSessions = data.sessions || [];

    if (fetchedSessions.length === 0) {
      // No sessions in this range, but that's okay
      return;
    }

    // Helper function to safely parse JSON strings from backend
    const parseJsonField = (field: any) => {
      if (field === null || field === undefined) {
        return null;
      }
      if (typeof field === 'string') {
        // Empty string or literal "null" should just return null
        if (field === '' || field === 'null') {
          return null;
        }
        try {
          return JSON.parse(field);
        } catch (err) {
          // Only log in dev mode for actual unexpected parse failures
          if (__DEV__) {
            console.warn('Failed to parse JSON field:', field.substring(0, 50));
          }
          return null;
        }
      }
      return field;
    };

    // Manually hydrate sessions into WatermelonDB (like fetch-session pattern)
    // Mark them as 'synced' to prevent them from being pushed back to server
    await database.write(async () => {
      const sessionsCollection = database.get(Session.table);
      const recordsToCreate = [];

      for (const item of fetchedSessions) {
        try {
          // Check if session already exists
          const existing = await sessionsCollection.find(item.id);
          // If it exists, skip it (don't overwrite)
          if (existing) {
            continue;
          }
        } catch (e) {
          // Session doesn't exist, prepare it for creation
          const preparedRecord = sessionsCollection.prepareCreate(
            (entity: any) => {
              entity._raw.id = item.id;
              entity.appointmentId = item.appointment_id;
              entity.partition = item._partition;
              entity.userId = item.user_id;
              entity.supervisingId = item.supervising_id;
              entity.patientId = item.patient_id;
              entity.claimId = item.claim_id;
              entity.renderingId = item.rendering_id;
              entity.type = item.type;
              entity.location = item.location;
              entity.date = item.date;
              entity.cpt = parseJsonField(item.cpt);
              entity.sessionDate = item.session_date;
              entity.startTimestamp = item.start_timestamp;
              entity.endTimestamp = item.end_timestamp;
              entity.editedStartTimestamp = item.edited_start_timestamp;
              entity.editedEndTimestamp = item.edited_end_timestamp;
              entity.note = item.note;
              entity.narrativeAltered = item.narrative_altered;
              entity.noteEditedTimestamp = item.note_edited_timestamp;
              entity.noteEditedBy = item.note_edited_by;
              entity.showAuthorsAndTimestamps =
                item.show_authors_and_timestamps;
              entity.therapistSignature = item.therapist_signature;
              entity.therapistSignature2 = item.therapist_signature_2;
              entity.supervisionSignature = item.supervision_signature;
              entity.parentSignature1 = item.parent_signature_1;
              entity.parentSignature2 = item.parent_signature_2;
              entity.attestationTimestamp = item.attestation_timestamp;
              entity.submissionTimestamp = item.submission_timestamp;
              entity.postSessionEmotions = parseJsonField(
                item.post_session_emotions,
              );
              entity.postSessionImpressions = item.post_session_impressions;
              entity.noteTemplateId = item.note_template_id;
              entity.noteTemplateVersionId = item.note_template_version_id;
              entity.additionalFields = parseJsonField(item.additional_fields);
              entity.address = item.address;
              entity.customAddress = parseJsonField(item.custom_address);
              entity.isAmending = item.is_amending;
              entity.amendmentReason = item.amendment_reason;
              entity.amendedBy = item.amended_by;
              entity.amendedAt = item.amended_at;
              entity.createdBy = item.created_by;
              entity.createdAt = item.created_at;
              entity.updatedBy = item.updated_by;
              entity.updatedAt = item.updated_at;
              entity.deletedBy = item.deleted_by;
              entity.deletedAt = item.deleted_at;
            },
          );

          // Mark as synced so it won't be pushed back to server
          preparedRecord._raw._status = 'synced';
          preparedRecord._raw._changed = '';

          recordsToCreate.push(preparedRecord);
        }
      }

      // Batch save all prepared records
      await database.batch(...recordsToCreate);
    });
  } catch (e) {
    console.error('Error fetching and hydrating sessions:', e);
    throw e;
  }
}
